import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setIsRefreshing } from '../storeRedux';

/**
 * Хук для защищенного выполнения периодических запросов
 *
 * Предотвращает выполнение нового запроса, если предыдущий еще не завершился.
 * Использует настраиваемый интервал из Redux store.
 *
 * @param {Function} callback - Async функция для выполнения
 * @param {Array} dependencies - Зависимости для useEffect (кроме refreshInterval)
 * @returns {Object} { isFetching: RefObject } - Ref с флагом выполнения запроса
 *
 * @example
 * const getPayoutsData = useCallback(async () => {
 *   // fetch logic here
 * }, [deps]);
 *
 * useProtectedInterval(getPayoutsData, [pageIndex, pageSize]);
 */
export const useProtectedInterval = (callback, dependencies = []) => {
  const isFetching = useRef(false);
  const intervalRef = useRef(null);
  const refreshInterval = useSelector(state => state.REFRESH_INTERVAL);
  const dispatch = useDispatch();

  const protectedCallback = useCallback(async () => {
    // Если запрос уже выполняется - пропускаем
    if (isFetching.current) {
      console.log('[useProtectedInterval] Запрос уже выполняется, пропускаем');
      return;
    }

    isFetching.current = true;
    dispatch(setIsRefreshing(true));

    try {
      await callback();
    } catch (error) {
      console.error('[useProtectedInterval] Ошибка при выполнении callback:', error);
    } finally {
      isFetching.current = false;
      dispatch(setIsRefreshing(false));
    }
  }, [callback, dispatch]);

  useEffect(() => {
    // Первый вызов сразу
    protectedCallback();

    // Устанавливаем интервал
    intervalRef.current = setInterval(protectedCallback, refreshInterval);

    // Очистка при размонтировании или изменении зависимостей
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [protectedCallback, refreshInterval, ...dependencies]);

  return { isFetching };
};
